/**
 * Onglet "Alertes & Promotions" du Super Admin Crédits (V31).
 *
 * Permet de :
 * - Configurer le seuil d'alerte solde bas, l'heure du cron, les emails BCC
 * - Configurer le bonus Cooper (montant, seuil parrainages, jour/heure du cron)
 * - Activer/désactiver chaque mécanisme indépendamment
 * - Déclencher manuellement les jobs (test)
 * - Voir les alertes envoyées et les Coopers récompensés
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Save, Loader2, Bell, Gift, RefreshCw, Mail, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CreditAlertsAdminTab = ({ getAuthHeader }) => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [bonusHist, setBonusHist] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cfg, st, al, bh] = await Promise.all([
        axios.get(`${API}/api/admin/credit-alerts`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/credit-alerts/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/credit-alerts/alerts?limit=20`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/credit-alerts/cooper-bonus/history?limit=20`, { headers: getAuthHeader() }),
      ]);
      setConfig(cfg.data?.config);
      setStats(st.data);
      setAlerts(al.data?.items || []);
      setBonusHist(bh.data?.items || []);
    } catch {
      toast.error('Erreur de chargement de la configuration');
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const updateLB = (key, value) => setConfig((c) => ({ ...c, low_balance: { ...c.low_balance, [key]: value } }));
  const updateCB = (key, value) => setConfig((c) => ({ ...c, cooper_bonus: { ...c.cooper_bonus, [key]: value } }));

  const save = async () => {
    if (!config) return;
    try {
      setSaving(true);
      const payload = {
        low_balance: { ...config.low_balance },
        cooper_bonus: { ...config.cooper_bonus },
      };
      // Convertir email_overrides en liste si string
      if (typeof payload.low_balance.email_overrides === 'string') {
        payload.low_balance.email_overrides = payload.low_balance.email_overrides
          .split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
      }
      const res = await axios.put(`${API}/api/admin/credit-alerts`, payload, { headers: getAuthHeader() });
      setConfig(res.data?.config);
      toast.success('Configuration sauvegardée — schedulers mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const runManual = async (kind) => {
    const url = kind === 'low_balance'
      ? `${API}/api/wallets/admin/credit-scheduler/scan-low-balance`
      : `${API}/api/wallets/admin/credit-scheduler/cooper-bonus`;
    try {
      setRunning(kind);
      const res = await axios.post(url, {}, { headers: getAuthHeader() });
      if (kind === 'low_balance') {
        toast.success(`Scan terminé : ${res.data?.alerted || 0} alerte(s) envoyée(s)`);
      } else {
        toast.success(`Bonus distribué : ${res.data?.rewarded_count || 0} Cooper(s)`);
      }
      load();
    } catch {
      toast.error('Erreur lors de l\'exécution');
    } finally {
      setRunning(null);
    }
  };

  if (!config) {
    return <div className="py-8 text-center text-sm text-slate-400">Chargement…</div>;
  }

  return (
    <div className="space-y-4" data-testid="credit-alerts-admin-tab">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Alertes envoyées (30j)</p>
          <p className="text-2xl font-bold text-amber-500">{stats?.nb_alerts_30j ?? 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Coopers récompensés (30j)</p>
          <p className="text-2xl font-bold text-emerald-600">{stats?.nb_coopers_recompenses_30j ?? 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Crédits distribués (30j)</p>
          <p className="text-2xl font-bold text-indigo-600">{stats?.credits_distribues_30j ?? 0}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="low_balance">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="low_balance" className="flex items-center gap-2" data-testid="alerts-tab-low-balance">
            <Bell className="w-4 h-4" /> Alerte solde bas
          </TabsTrigger>
          <TabsTrigger value="cooper_bonus" className="flex items-center gap-2" data-testid="alerts-tab-cooper-bonus">
            <Gift className="w-4 h-4" /> Bonus Cooper actifs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="low_balance" className="space-y-4">
          <Card className="border-l-4 border-l-amber-400">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-500" /> Configuration alerte solde bas
              </CardTitle>
              <CardDescription>
                Email envoyé automatiquement quand un utilisateur a un solde inférieur à <strong>{config.low_balance.seuil_pct}%</strong>
                {' '}de sa consommation des 30 derniers jours. Cron exécuté quotidiennement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-amber-50/60 rounded-lg">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config.low_balance.enabled === true}
                    onCheckedChange={(v) => updateLB('enabled', v)}
                    data-testid="lb-enabled-switch"
                  />
                  <span className="text-sm font-medium">
                    {config.low_balance.enabled ? 'Alertes activées' : 'Alertes désactivées'}
                  </span>
                </div>
                <Badge variant={config.low_balance.enabled ? 'default' : 'secondary'}>
                  {config.low_balance.enabled ? 'Actif' : 'Inactif'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Seuil (% du besoin)</Label>
                  <Input type="number" min={1} max={100} value={config.low_balance.seuil_pct}
                    onChange={(e) => updateLB('seuil_pct', parseInt(e.target.value || 0, 10))}
                    data-testid="lb-seuil-pct" />
                </div>
                <div>
                  <Label className="text-xs">Cron heure</Label>
                  <Input type="number" min={0} max={23} value={config.low_balance.cron_hour}
                    onChange={(e) => updateLB('cron_hour', parseInt(e.target.value || 0, 10))}
                    data-testid="lb-cron-hour" />
                </div>
                <div>
                  <Label className="text-xs">Cron minute</Label>
                  <Input type="number" min={0} max={59} value={config.low_balance.cron_minute}
                    onChange={(e) => updateLB('cron_minute', parseInt(e.target.value || 0, 10))}
                    data-testid="lb-cron-minute" />
                </div>
                <div>
                  <Label className="text-xs">Idempotence (heures)</Label>
                  <Input type="number" min={1} max={168} value={config.low_balance.idempotence_hours}
                    onChange={(e) => updateLB('idempotence_hours', parseInt(e.target.value || 0, 10))}
                    data-testid="lb-idempotence" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={config.low_balance.email_enabled === true}
                  onCheckedChange={(v) => updateLB('email_enabled', v)}
                  data-testid="lb-email-enabled"
                />
                <span className="text-sm flex items-center gap-1">
                  <Mail className="h-4 w-4" /> Envoyer un email aux utilisateurs concernés
                </span>
              </div>

              <div>
                <Label className="text-xs">Emails additionnels en BCC (séparés par virgule)</Label>
                <Input
                  value={Array.isArray(config.low_balance.email_overrides) ? config.low_balance.email_overrides.join(', ') : (config.low_balance.email_overrides || '')}
                  onChange={(e) => updateLB('email_overrides', e.target.value)}
                  placeholder="contact@objectifscopoutremer.com, equipe@..."
                  data-testid="lb-email-overrides"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Ces adresses seront mises en BCC pour suivi manager. Vide = aucun.
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="alerts-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Sauvegarder & reprogrammer
                </Button>
                <Button variant="outline" onClick={() => runManual('low_balance')} disabled={running === 'low_balance'} data-testid="lb-run-now-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${running === 'low_balance' ? 'animate-spin' : ''}`} />
                  Exécuter maintenant
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Dernières alertes envoyées</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-3" data-testid="lb-alerts-empty">Aucune alerte pour le moment.</p>
              ) : (
                <div className="divide-y" data-testid="lb-alerts-list">
                  {alerts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs py-2">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-700 truncate">{a.user_email || a.user_id}</p>
                        <p className="text-[11px] text-slate-400">
                          Solde {a.solde} / seuil {a.seuil} · conso 30j: {a.consommation_30j}
                        </p>
                      </div>
                      <span className="text-slate-400">{a.created_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cooper_bonus" className="space-y-4">
          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-emerald-500" /> Configuration bonus Cooper actifs
              </CardTitle>
              <CardDescription>
                Bonus mensuel de <strong>{config.cooper_bonus.bonus_credits} crédits</strong> aux Coopers
                ayant ≥ <strong>{config.cooper_bonus.min_parrainages}</strong> parrainages signés sur {config.cooper_bonus.period_days} jours.
                Idempotent par mois (1 bonus / Cooper / mois).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-emerald-50/60 rounded-lg">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config.cooper_bonus.enabled === true}
                    onCheckedChange={(v) => updateCB('enabled', v)}
                    data-testid="cb-enabled-switch"
                  />
                  <span className="text-sm font-medium">
                    {config.cooper_bonus.enabled ? 'Bonus mensuel activé' : 'Bonus mensuel désactivé'}
                  </span>
                </div>
                <Badge variant={config.cooper_bonus.enabled ? 'default' : 'secondary'}>
                  {config.cooper_bonus.enabled ? 'Actif' : 'Inactif'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Montant bonus (crédits)</Label>
                  <Input type="number" min={0} value={config.cooper_bonus.bonus_credits}
                    onChange={(e) => updateCB('bonus_credits', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-bonus-credits" />
                </div>
                <div>
                  <Label className="text-xs">Min. parrainages</Label>
                  <Input type="number" min={0} value={config.cooper_bonus.min_parrainages}
                    onChange={(e) => updateCB('min_parrainages', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-min-parrainages" />
                </div>
                <div>
                  <Label className="text-xs">Fenêtre (jours)</Label>
                  <Input type="number" min={1} max={365} value={config.cooper_bonus.period_days}
                    onChange={(e) => updateCB('period_days', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-period-days" />
                </div>
                <div>
                  <Label className="text-xs">Cron jour du mois (1-28)</Label>
                  <Input type="number" min={1} max={28} value={config.cooper_bonus.cron_day}
                    onChange={(e) => updateCB('cron_day', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-cron-day" />
                </div>
                <div>
                  <Label className="text-xs">Cron heure</Label>
                  <Input type="number" min={0} max={23} value={config.cooper_bonus.cron_hour}
                    onChange={(e) => updateCB('cron_hour', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-cron-hour" />
                </div>
                <div>
                  <Label className="text-xs">Cron minute</Label>
                  <Input type="number" min={0} max={59} value={config.cooper_bonus.cron_minute}
                    onChange={(e) => updateCB('cron_minute', parseInt(e.target.value || 0, 10))}
                    data-testid="cb-cron-minute" />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="alerts-save-btn-cb">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Sauvegarder & reprogrammer
                </Button>
                <Button variant="outline" onClick={() => runManual('cooper_bonus')} disabled={running === 'cooper_bonus'} data-testid="cb-run-now-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${running === 'cooper_bonus' ? 'animate-spin' : ''}`} />
                  Exécuter maintenant
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Historique des Coopers récompensés</CardTitle>
            </CardHeader>
            <CardContent>
              {bonusHist.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-3 flex items-center justify-center gap-2" data-testid="cb-history-empty">
                  <AlertCircle className="h-4 w-4" /> Aucun Cooper récompensé pour le moment.
                </p>
              ) : (
                <div className="divide-y" data-testid="cb-history-list">
                  {bonusHist.map((b) => (
                    <div key={b.id} className="flex items-center justify-between text-xs py-2">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-700 truncate">
                          {b.prenom} {b.nom} <span className="text-slate-400">— {b.email}</span>
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {b.month_key} · {b.parrainages} parrainage(s)
                        </p>
                      </div>
                      <Badge className="bg-emerald-600 text-white font-semibold">+{b.bonus} cr.</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CreditAlertsAdminTab;
